import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Stack,
  useTheme,
  IconButton,
  TextField,
} from '@mui/material';
import { Backspace } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface NumberPadProps {
  value: string;
  onChange: (value: string) => void;
  maxValue?: number;
  allowDecimal?: boolean;
  presets?: number[];
}

/**
 * Mobile-optimized number pad for cash entry
 * Large buttons, one-handed operation
 */
const NumberPad: React.FC<NumberPadProps> = ({
  value,
  onChange,
  maxValue,
  allowDecimal = true,
  presets = [10, 20, 50, 100],
}) => {
  const theme = useTheme();
  const { t } = useTranslation('cashReconciliation');

  const handleDigit = (digit: string) => {
    // Prevent multiple decimal points
    if (digit === '.' && value.includes('.')) return;

    // Limit decimal places to 2
    if (value.includes('.') && value.split('.')[1]?.length >= 2) return;

    const newValue = value === '0' && digit !== '.' ? digit : value + digit;

    // Check max value
    if (maxValue && parseFloat(newValue) > maxValue) return;

    onChange(newValue);
  };

  const handleBackspace = () => {
    if (value.length === 1) {
      onChange('0');
    } else {
      onChange(value.slice(0, -1));
    }
  };

  const handleClear = () => {
    onChange('0');
  };

  const handlePreset = (amount: number) => {
    onChange(amount.toString());
  };

  const buttons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', allowDecimal ? '.' : '', '0', '00'];

  return (
    <Box>
      {/* Display */}
      <Box
        sx={{
          bgcolor: theme.palette.custom.gray[100],
          borderRadius: 2,
          p: 3,
          mb: 2,
          textAlign: 'right',
        }}
      >
        <Typography variant="displayLG"
          fontWeight={700}
          color="primary">
          £{value}
        </Typography>
      </Box>

      {/* Preset Amounts */}
      {presets.length > 0 && (
        <Stack direction="row"
          spacing={1}
          mb={2}>
          {presets.map((amount) => (
            <Button
              key={amount}
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => handlePreset(amount)}
              sx={{ fontSize: '1.1rem' }}
            >
              £{amount}
            </Button>
          ))}
        </Stack>
      )}

      {/* Number Pad */}
      <Grid container
        spacing={1}>
        {buttons.map((btn, index) => (
          <Grid item
            xs={4}
            key={index}>
            {btn ? (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => handleDigit(btn)}
                sx={{
                  minHeight: '72px',
                  fontSize: '1.8rem',
                  fontWeight: 600,
                  bgcolor: theme.palette.custom.gray[200],
                  color: theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: theme.palette.custom.gray[300],
                  },
                  '&:active': {
                    bgcolor: theme.palette.custom.gray[400],
                  },
                }}
              >
                {btn}
              </Button>
            ) : (
              <Box sx={{ minHeight: '72px' }} /> // Spacer
            )}
          </Grid>
        ))}

        {/* Backspace Button */}
        <Grid item
          xs={4}>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleBackspace}
            sx={{
              minHeight: '72px',
              bgcolor: theme.palette.error.light,
              '&:hover': {
                bgcolor: theme.palette.error.main,
              },
            }}
          >
            <Backspace sx={{ fontSize: 32 }} />
          </Button>
        </Grid>

        {/* Clear Button */}
        <Grid item
          xs={4}>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleClear}
            sx={{
              minHeight: '72px',
              bgcolor: theme.palette.warning.light,
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.warning.main,
              },
            }}
          >
            {t('mobile.numberPad.clear', 'Clear')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NumberPad;
